"use strict";
/**
 * The shopping cart module provides access to shopping cart data from the EVA back-end
 * Note that the shopping cart is bound to the current session so there is only 1 of them
 * You can regard it as the active order
 * @preferred
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
var _ = require("lodash");
var service_1 = require("../service");
var settings_1 = require("../settings");
var base_1 = require("./base");
var orders_1 = require("./orders");
var payments_1 = require("./payments");
var products_1 = require("./products");
/**
 * Provides access to the shopping cart available in EVA
 */
var ShoppingCart = /** @class */ (function (_super) {
    __extends(ShoppingCart, _super);
    /**
     * This class wraps the current shopping cart from EVA and the operations you can perform on it.
     *
     * @return {void} This method doesn't return anything
     */
    function ShoppingCart() {
        var _this = _super.call(this) || this;
        _this.payment = new payments_1.Payments(_this);
        return _this;
    }
    /**
     * Returns if the order state 'completed' applies to the order
     * @return {boolean} Indicates the state is active
     */
    ShoppingCart.prototype.isCompleted = function () {
        return this._isCompleted === true;
    };
    /**
     * Returns if the order state 'shipped' applies to the order
     * @return {boolean} Indicates the state is active
     */
    ShoppingCart.prototype.isShipped = function () {
        return this._isShipped === true;
    };
    /**
     * Returns if the order state 'paid' applies to the order
     * @return {boolean} Indicates the state is active
     */
    ShoppingCart.prototype.isPaid = function () {
        return this._isPaid === true;
    };
    /**
     * Returns if the order state 'invoiced' applies to the order
     * @return {boolean} Indicates the state is active
     */
    ShoppingCart.prototype.isInvoiced = function () {
        return this._isInvoiced === true;
    };
    /**
     * Returns if the order state 'cancelled' applies to the order
     * @return {boolean} Indicates the state is active
     */
    ShoppingCart.prototype.isCancelled = function () {
        return this._isCancelled === true;
    };
    /**
     * Returns the current open amount still to be paid for the shopping cart
     * @return {number}
     */
    ShoppingCart.prototype.getOpenAmount = function () {
        return this.openAmount;
    };
    /**
     * Returns the total value of all the items in the shopping cart with VAT
     * @return {number} The value of the items in the shopping cart with VAT
     */
    ShoppingCart.prototype.getTotalAmountInTax = function () {
        return this.totalAmountInTax;
    };
    /**
     * Returns the total value of all the items in the shopping cart without VAT
     * @return {number} The value of the items in the shopping cart without VAT
     */
    ShoppingCart.prototype.getTotalAmount = function () {
        return this.totalAmount;
    };
    /**
     * Return order lines by type
     * @return {Array<EVA.Core.OrderLineDto>} Array of order lines
     */
    ShoppingCart.prototype.getOrderLinesByType = function (orderLineType) {
        return _.filter(this.data.Lines, function (line) {
            return line.Type === orderLineType;
        });
    };
    /**
     * Return the total amount of shipping costs
     * @return {number} The total value of the shipping costs
     */
    ShoppingCart.prototype.getTotalShippingAmount = function (inTax) {
        if (inTax === void 0) { inTax = true; }
        var targetField;
        if (inTax) {
            targetField = "TotalAmountInTax";
        }
        else {
            targetField = "TotalAmount";
        }
        return _.sumBy(this.getOrderLinesByType(5), targetField);
    };
    /**
     * Return the total amount of ShippingCosts
     * @return {number} The total value of the shipping costs
     */
    ShoppingCart.prototype.getTotalDiscountAmount = function (inTax) {
        if (inTax === void 0) { inTax = true; }
        var targetField;
        if (inTax) {
            targetField = "TotalAmountInTax";
        }
        else {
            targetField = "TotalAmount";
        }
        return _.sumBy(this.getOrderLinesByType(1), targetField);
    };
    /**
     * Return the current order amounts
     *
     * @returns {EVA.Core.OrderAmounts}
     * @memberof ShoppingCart
     */
    ShoppingCart.prototype.getOrderAmounts = function () {
        return this._orderAmounts;
    };
    Object.defineProperty(ShoppingCart.prototype, "stockOrganizationUnits", {
        /**
         * Returns the list of different organizations where orderline items are te be picked up
         * @return {number} The list of organizations
         */
        get: function () {
            return this._stockOrganizationUnits;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ShoppingCart.prototype, "requestedDates", {
        /**
         * Returns the list of different organizations where orderline items are te be picked up
         * @return {number} The list of organizations
         */
        get: function () {
            return this._requestedDates;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the discount invalid reasons
     * @return {number}
     */
    ShoppingCart.prototype.getMessages = function () {
        return this._messages;
    };
    /**
     * Set the product properties to use
     * @return {void}
     */
    ShoppingCart.prototype.setProductProperties = function (properties) {
        this.productProperties = properties;
    };
    /**
     * Returns the product properties that are active
     * @return {string[]}
     */
    ShoppingCart.prototype.getProductProperties = function () {
        return this.productProperties;
    };
    /**
     * Fetches the shopping cart details
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetShoppingCart, EVA.Core.ShoppingCartResponse>>} A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.fetch = function () {
        // Build the request data
        // Set the optional product properties, if available or defaults from settings
        //
        var requestCartData = {
            ProductProperties: this.productProperties ? this.productProperties : settings_1.SettingsManager.getProductProperties(),
        };
        return _super.prototype.fetch.call(this, "GetShoppingCart", requestCartData);
    };
    /**
     * Sets the current item details using the fetched service response.
     * Extending classes should override this method to pull the details from response
     * @param  {EVA.Core.ShoppingCartResponse}  response    The response from the service call
     * @return {EVA.Core.OrderDto}              The order details
     */
    ShoppingCart.prototype.setData = function (response) {
        // Collect the data exposed outside of the OrderDto
        //
        this.openAmount = response.OpenAmount || 0;
        this.totalAmount = response.TotalAmount || 0;
        this.totalAmountInTax = response.TotalAmountInTax || 0;
        this.foreignOpenAmount = response.ForeignOpenAmount || 0;
        this._isCompleted = response.ShoppingCart.IsCompleted === true;
        this._isShipped = response.ShoppingCart.IsShipped === true;
        this._isPaid = response.ShoppingCart.IsPaid === true;
        this._isInvoiced = response.ShoppingCart.IsInvoiced === true;
        this._isCancelled = response.ShoppingCart.IsCancelled === true;
        this._messages = response.ShoppingCart.Messages || [];
        this._orderAmounts = response.Amounts;
        // Set the shopping cart data
        //
        this.data = response.ShoppingCart;
        // Set or update the ID
        //
        this.id = this.data.ID;
        // Collect the stock organizations from the order lines
        // This used to be per line but is now centralised on the order
        //
        this._stockOrganizationUnits = [this.data.OrganizationUnitID];
        // Collect the requested delivery dates from the order lines
        //
        this._requestedDates = _.without(_.uniq(_.map(this.data.Lines, function (line) {
            if (line.LineActionType === 4 && line.RequestedDate) {
                return line.RequestedDate;
            }
        })), undefined);
        // Check if the order is a pickup order
        //
        var pickupOrder = this._stockOrganizationUnits.length < 2;
        if (pickupOrder) {
            _.forEach(this.data.Lines, function (line) {
                pickupOrder = pickupOrder && line.LineActionType === 1;
            });
        }
        this.isPickupOrder = pickupOrder;
        return this.data;
    };
    /**
     * Adds a product to the shopping cart
     * @param  {number|Product}                 product         Either a product id or a product class to add to the shopping cart
     * @param  {number}                         quantity        Quantity to add
     * @param  {EVA.Core.LineActionTypes}       lineActionType  Line action type. Default is 4 (delivery), 1 is for reservation
     * @return {Promise}                                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.addProduct = function (product, quantity, lineActionType) {
        var _this = this;
        if (quantity === void 0) { quantity = 1; }
        if (lineActionType === void 0) { lineActionType = 4; }
        // You add a product to the shopping cart either by its id or by feeding us an
        // instance of the SDK product class
        //
        var productId;
        if (product instanceof products_1.Product) {
            productId = product.getId();
        }
        else {
            productId = product;
        }
        // Create our service instance
        //
        var service = new service_1.EVAService("AddProductToOrder");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        // Note that webshop can only reserve or ask for delivery of an items
        // So only the following LineActionTypes are allowed:
        //
        // EVA.Core.LineActionTypes.ReserveLine: 1
        // EVA.Core.LineActionTypes.Delivery:    4
        //
        var requestData = {
            ProductID: productId,
            QuantityOrdered: quantity,
            LineActionType: lineActionType,
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Changes an order line to a stock reservation
     * @param  {number}     orderLineId     The order line id that contains the product(s) that is/are to be reserverd
     * @param  {number}     organizationID  The shop to reserve the product at
     * @return {Promise}                    A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.reserveOrderLine = function (orderLineId) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("ModifyLineActionType");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderLineID: orderLineId,
            LineActionType: 1
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Changes an order line to be shipped
     * @param  {number}     orderLineId     The order line id that contains the product(s) that is/are to be reserverd
     * @return {Promise}                    A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.shipOrderLine = function (orderLineId) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("ModifyLineActionType");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderLineID: orderLineId,
            LineActionType: 3
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Changes an order line to be delivered
     * @param  {number}     orderLineId     The order line id that contains the product(s) that is/are to be reserverd
     * @return {Promise}                    A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.deliverOrderLine = function (orderLineId) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("ModifyLineActionType");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderLineID: orderLineId,
            LineActionType: 4
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Removes order lines from the shopping cart
     * @param  {number|Array<number>}       orderLineId     The identifier or array of identifiers of the order line(s) to remove from the shopping cart
     * @return {Promise}                                    A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.cancelOrderLine = function (orderLineId) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("CancelOrderLine");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderLineID: orderLineId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Convenience methods to remove all instances of a product from the shopping  This is the more direct mirror call of addProduct
     * @param  {number|Product}     product     Either a product id or a product class to remove from the shopping cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.removeProduct = function (product) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // You remove a product from the shopping cart either by its id or by feeding us an
            // instance of the SDK product class
            //
            var productId;
            if (product instanceof products_1.Product) {
                productId = product.getId();
            }
            else {
                productId = product;
            }
            // Collect the order line id's where this product is present
            //
            var orderLineIds = [];
            if (_.isArray(_this.data.Lines)) {
                _.forEach(_this.data.Lines, function (orderLine) {
                    if (orderLine.ProductID === productId) {
                        // Create an inline service call because the local method will refresh the cart each time
                        //
                        var service = new service_1.EVAService("CancelOrderLine");
                        var requestData = {
                            OrderLineID: orderLine.ID
                        };
                        orderLineIds.push(service.call(requestData));
                    }
                });
            }
            Promise.all(orderLineIds)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    resolve(cartResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Modify the number of items for an order line
     * @param  {number}         orderLineId     The identifier of the order line to modify from the shopping cart
     * @param  {number}         quantity        The new quantity
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.modifyQuantity = function (orderLineId, quantity) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("ModifyQuantityOrdered");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            OrderLineID: orderLineId,
            NewQuantityOrdered: quantity
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Clears the entire shopping basket
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetShoppingCart, EVA.Core.ShoppingCartResponse>>} A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.empty = function () {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("DetachOrderFromSession");
            // Perform the call and capture the resulting promise
            //
            service.call()
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * List the available shipping methods for the current cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.listShippingMethods = function (orderId, orderlineIds) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("ListAvailableShippingMethods");
            var requestData = {
                OrderID: orderId,
                OrderlineIDs: orderlineIds
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function (result) {
                resolve(result.response);
            })
                .catch(reject);
        });
    };
    /**
     * Gets the shipping methods currently set to the cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.getShippingMethods = function (orderId) {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetShippingMethodsForOrder");
            var requestData = {
                OrderID: orderId
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function (result) {
                resolve(result.response);
            })
                .catch(reject);
        });
    };
    /**
     * Sets the pickup organiszation unit to the cart.
     * We don't know the orderId, but the session Id will do the trick
     * @return {Promise} A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.setPickupOrganizationUnit = function (organizationUnitID) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetPickupOrganizationUnit");
            var requestData = {
                OrganizationUnitID: organizationUnitID,
                OrderID: undefined,
                SessionID: undefined,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Gets the shipping methods currently set to the cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.setShippingMethod = function (orderId, orderLineId, shippingMethodId, requestDeliveryDate) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetShippingMethod");
            var requestData = {
                OrderID: orderId,
                // OrderLineID:            orderLineId,
                shippingMethodId: shippingMethodId,
            };
            if (orderLineId) {
                requestData.OrderLineID = orderLineId;
            }
            if (requestDeliveryDate) {
                requestData.RequestDeliveryDate = requestDeliveryDate;
            }
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Modify the number of items for an order line
     * @param  {string}         couponCode      The coupon code that needs to be added to the cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.addCouponCode = function (couponCode) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("AddDiscountToOrder");
        // Build the request data
        // The base class will add things like SessionID so to keep typescript
        // happy we're using an 'any' type object here
        //
        var requestData = {
            CouponCode: couponCode
        };
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Add discount to an order
     * @param  {number}    discountId   The discount Id to use
     * @param  {number}    amount       The amount of discount that is required
     * @param  {string}    currencyId   The currency Id to use
     * @param  {string}    reason       (Optional) reason for giving discount
     * @return {Promise}                A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.addDiscount = function (discountParams) {
        var _this = this;
        // Create our service instance
        //
        var service = new service_1.EVAService("AddDiscountToOrder");
        var requestData = discountParams;
        // Perform the call and capture the resulting promise
        //
        return new Promise(function (resolve, reject) {
            service.call(requestData)
                .then(function () {
                _this.fetch().then(function (cartResponse) {
                    var finalResponse = __assign({}, cartResponse, { request: requestData });
                    resolve(finalResponse);
                }).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Retrieves the shopping cart summary information
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetShoppingCartInfo, EVA.Core.Services.ShoppingCartInfoResponse>>} A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.getInfo = function () {
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("GetShoppingCartInfo");
            // Perform the call
            //
            service.call().then(function (result) {
                resolve(result.response);
            }).catch(reject);
        });
    };
    /**
     * Attaches a customer to the shopping cart
     * @param  {LoggedInUser}       customer       The current user class that is to be set as the customer for the shopping cart
     * @return {Promise<IEVAServiceResponse<EVA.Core.Services.GetOrder, EVA.Core.Services.GetOrderResponse>>} A promise resolving when the fetch from the back-end has completed
     */
    ShoppingCart.prototype.setCustomer = function (customer) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("AttachCustomerToOrder");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                UserID: customer.data.ID,
                OrderID: _this.data.ID
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Places an order for the current shopping cart contents
     * @return {Promise<Order>} A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.createOrder = function (includePendingPayments, includeFailedPayments) {
        var _this = this;
        if (includePendingPayments === void 0) { includePendingPayments = false; }
        if (includeFailedPayments === void 0) { includeFailedPayments = false; }
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("PrepareOrderForCheckout");
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderId: _this.getId(),
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                var order = new orders_1.Order();
                order.id = _this.getId();
                order.includePendingPayments = includePendingPayments;
                order.includeFailedPayments = includeFailedPayments;
                order.fetch()
                    .then(function () { return resolve(order); })
                    .catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Set the shopping basket to an existing order. Removes any existing items in the shopping cart
     * @param  {number|Order}       order       Either an order id or an order class to set as the shopping cart
     * @return {Promise}                        A promise resolving when the call to the back-end has completed
     */
    ShoppingCart.prototype.setFromOrder = function (order) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("AttachOrderToSession");
            var orderId;
            if (order instanceof orders_1.Order) {
                orderId = order.getId();
            }
            else {
                orderId = order;
            }
            // Build the request data
            // The base class will add things like SessionID so to keep typescript
            // happy we're using an 'any' type object here
            //
            var requestData = {
                OrderID: orderId,
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    /**
     * Sets the remarks for an order
     *
     * @param {string} remarks The remarks content
     * @returns {Promise<IEVAServiceResponse<EVA.Core.Services.SetCustomOrderData, EVA.API.EmptyResponseMessage>>}
     * @memberof Order
     */
    ShoppingCart.prototype.setRemark = function (remarks) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            // Create our service instance
            //
            var service = new service_1.EVAService("SetRemarkOnOrder");
            // Build the request data
            //
            var requestData = {
                OrderID: _this.getId(),
                Remark: remarks
            };
            // Perform the call and capture the resulting promise
            //
            service.call(requestData)
                .then(function () {
                _this.fetch().then(resolve).catch(reject);
            })
                .catch(reject);
        });
    };
    return ShoppingCart;
}(base_1.FetchableItem));
exports.ShoppingCart = ShoppingCart;
